import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.backgroundSecondary} 25%,
    ${props => props.theme.isDark
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(35,37,98,0.06)'
    } 50%,
    ${props => props.theme.colors.backgroundSecondary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: ${props => props.theme.borderRadius.sm};
`;

export const SkeletonLine = styled(SkeletonBase)`
  height: ${props => props.height || '16px'};
  width: ${props => props.width || '100%'};
  margin-bottom: ${props => props.mb || '8px'};
`;

export const SkeletonCircle = styled(SkeletonBase)`
  width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const SkeletonBlock = styled(SkeletonBase)`
  height: ${props => props.height || '80px'};
  width: 100%;
  margin-bottom: ${props => props.mb || '12px'};
`;

// Table skeleton: header row + N data rows with cols columns
export const SkeletonTable = ({ rows = 5, cols = 4, theme }) => {
  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '16px',
        padding: '16px',
        borderBottom: `2px solid ${theme?.colors?.border || '#e2e8f0'}`,
      }}>
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonLine key={i} height="14px" width="60%" mb="0" theme={theme} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: '16px',
          padding: '16px',
          borderBottom: `1px solid ${theme?.colors?.border || '#e2e8f0'}`,
        }}>
          {Array.from({ length: cols }).map((_, colIdx) => (
            <SkeletonLine
              key={colIdx}
              height="14px"
              width={colIdx === 0 ? '40%' : colIdx === cols - 1 ? '30%' : '80%'}
              mb="0"
              theme={theme}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Cards skeleton: grid of skeleton cards
export const SkeletonCards = ({ count = 6, theme }) => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '24px',
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          backgroundColor: theme?.colors?.backgroundSecondary,
          border: `1px solid ${theme?.colors?.border || '#e2e8f0'}`,
          borderRadius: theme?.borderRadius?.lg || '12px',
          padding: '24px',
        }}>
          <SkeletonCircle size="40px" theme={theme} style={{ marginBottom: '16px' }} />
          <SkeletonLine height="18px" width="70%" mb="8px" theme={theme} />
          <SkeletonLine height="14px" width="90%" mb="4px" theme={theme} />
          <SkeletonLine height="14px" width="60%" mb="20px" theme={theme} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SkeletonLine height="12px" width="30%" mb="0" theme={theme} />
            <SkeletonLine height="12px" width="20%" mb="0" theme={theme} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Stats skeleton: row of stat boxes
export const SkeletonStats = ({ count = 4, theme }) => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${count}, 1fr)`,
      gap: '16px',
      marginBottom: '24px',
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          backgroundColor: theme?.colors?.backgroundSecondary,
          border: `1px solid ${theme?.colors?.border || '#e2e8f0'}`,
          borderRadius: theme?.borderRadius?.lg || '12px',
          padding: '24px',
        }}>
          <SkeletonLine height="12px" width="50%" mb="12px" theme={theme} />
          <SkeletonLine height="32px" width="60%" mb="8px" theme={theme} />
          <SkeletonLine height="12px" width="40%" mb="0" theme={theme} />
        </div>
      ))}
    </div>
  );
};
